import { ProductCarousel } from '@johnlewispartnership/wtr-content-component-library';
import type {
  CmsProductCarouselItem,
  CmsProductCarouselProps,
} from '@johnlewispartnership/wtr-content-component-library';
import type { TrolleyQuantity } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/TrolleyControls/types';

import { useFavouritesService } from 'services/favourites/useFavouritesService';
import type { CachedTrolley } from 'services/trolley/useTrolleyService';
import { useTrolleyService } from 'services/trolley/useTrolleyService';
import { usePreloadedData } from 'renderer/context/preloadedDataContext';
import { buildListContext, type CarouselItem } from 'renderer/utils/analytics';
import { useFavouritesAnalytics } from 'renderer/hooks/useFavouritesAnalytics';
import { useTrolleyAnalytics } from 'renderer/hooks/useTrolleyAnalytics';
import { useShoppingContext } from 'services/shopping-context/useShoppingContext';
import { CustomerOrderState } from 'services/shopping-context/types';
import type { PurchaseOrAmendType } from 'renderer/hooks/useTrackVirtualPageView';
import { useTrackedExperiments } from 'renderer/context/TrackedExperiments';
import { INSPIRATION_GENERIC_CONTENT_PRODUCT_CAROUSEL } from 'constants/experiments';

export const getTrolleyAmount = (trolley: CachedTrolley | undefined, lineNumber: string) =>
  trolley?.trolleyItems?.find(item => item.lineNumber === lineNumber)?.quantity?.amount ?? 0;

const ProductCarouselWrapper = ({
  items,
  ...rest
}: CmsProductCarouselProps & { productSource?: string }) => {
  const titleText = rest.title?.blocks[0]?.children?.[0]?.text.trim();
  const productSource = rest.productSource;

  const { conflicts } = usePreloadedData();
  const { data: shoppingContext } = useShoppingContext();
  const { favourites, toggleFavourite } = useFavouritesService();
  const { trolley, isReady, addTrolleyItem, updateTrolleyItem } = useTrolleyService();

  const getConflict = (item: CmsProductCarouselItem) =>
    conflicts?.find(conflict => conflict.productId === item.searchProduct.id);

  const enrichedItems = items.map(item => {
    const trolleyMatchedItem = trolley?.trolleyItems?.find(
      trolleyItem => trolleyItem.lineNumber === item.searchProduct.lineNumber,
    );

    return {
      ...item,
      searchProduct: {
        ...item.searchProduct,
        favourite: !!favourites[item.searchProduct.lineNumber || ''],
        availability: getConflict(item)?.outOfStock ? 'sold out online' : 'available',
      },
      trolleyQuantity: trolleyMatchedItem?.quantity,
    };
  });

  const purchaseOrAmend: PurchaseOrAmendType =
    shoppingContext?.customerOrderState === CustomerOrderState.AMENDING ? 'amend' : 'purchase';

  // ToDo: WPIP-83492 - remove cast once CCL types include analytics fields
  const analyticsItems = enrichedItems as CarouselItem[];
  const listContext = buildListContext(purchaseOrAmend, productSource, titleText);

  const favouritesAnalytics = useFavouritesAnalytics(analyticsItems, listContext);
  const trolleyAnalytics = useTrolleyAnalytics(analyticsItems, listContext);

  const handleFavouriteClick = (lineNumber: string) => {
    if (favourites[lineNumber]) {
      favouritesAnalytics.trackRemove(lineNumber);
    } else {
      favouritesAnalytics.trackAdd(lineNumber);
    }

    toggleFavourite(lineNumber);
  };

  const handleAddTrolleyItem = (quantity: TrolleyQuantity, lineNumber: string) => {
    addTrolleyItem(quantity, lineNumber);
    trolleyAnalytics.trackAdd(quantity.amount, lineNumber);
  };

  const handleUpdateTrolleyItem = (quantity: TrolleyQuantity, lineNumber: string) => {
    updateTrolleyItem(quantity, lineNumber);

    const previousAmount = getTrolleyAmount(trolley, lineNumber);
    const diffAmount = quantity.amount - previousAmount;

    if (diffAmount > 0) {
      trolleyAnalytics.trackAdd(diffAmount, lineNumber);
    } else if (diffAmount < 0) {
      trolleyAnalytics.trackRemove(Math.abs(diffAmount), lineNumber);
    }
  };

  return (
    <ProductCarousel
      {...rest}
      items={enrichedItems}
      onFavouriteClick={handleFavouriteClick}
      onAddToTrolley={isReady ? handleAddTrolleyItem : undefined}
      onUpdateTrolleyQuantity={isReady ? handleUpdateTrolleyItem : undefined}
      conflicts={conflicts}
    />
  );
};

const ProductCarouselWrapperWithFeatureFlag = (
  props: React.ComponentProps<typeof ProductCarouselWrapper>,
) => {
  const { getExperimentById } = useTrackedExperiments();

  const showProductCarousel =
    getExperimentById(INSPIRATION_GENERIC_CONTENT_PRODUCT_CAROUSEL.experiment)?.variant ===
    INSPIRATION_GENERIC_CONTENT_PRODUCT_CAROUSEL.variant.showProductCarousel;

  if (!showProductCarousel) {
    return null;
  }

  return <ProductCarouselWrapper {...props} />;
};

export default ProductCarouselWrapperWithFeatureFlag;
